import React, { useEffect, useImperativeHandle, useRef, useState } from "react"
import { Pressable } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { theme } from "@common/theme/theme"
import TextM from "@common/components/text/TextM"
import { useDispatch, useSelector } from "react-redux"
import { setFocusScreenShare } from "@data/redux/actions/liveMode"
import { useLocalParticipant, useRoomContext } from "@livekit/components-react"
import { LocalVideoTrack, Track } from "livekit-client"

const isCropSupported = typeof CropTarget !== "undefined"

// Module-level: persists across component mount/unmount cycles.
// The button unmounts and remounts when isFocusShareActive toggles
// (standalone → segmented group), so a useRef would be lost.
let activeCropTrack = null

const ScreenShareToggleButton = React.forwardRef(({ style, toolbarStyle, cropTargetRef,hideIcon, label, fontSize: fontSizeProp, fontWeight: fontWeightProp, overrideColor }, ref) => {
    const dispatch = useDispatch()
    const room = useRoomContext()
    const { isScreenShareEnabled } = useLocalParticipant()
    const isFocusScreenShare = useSelector(
        (state) => state.liveMode?.isFocusScreenShare
    )
    const [toggling, setToggling] = useState(false)
    const prevFocusRef = useRef(isFocusScreenShare)

    // Sync Redux if screen share is stopped externally (browser "Stop sharing" button)
    useEffect(() => {
        if (!isScreenShareEnabled && isFocusScreenShare && !toggling) {
            activeCropTrack = null
            dispatch(setFocusScreenShare(false))
        }
    }, [isScreenShareEnabled])

    // Handle external stop request (e.g., tutor confirmed interruption from dialog)
    // Only fires on true → false transition, not on initial mount, to avoid
    // killing a control-bar screen share when this component (re)mounts.
    useEffect(() => {
        if (prevFocusRef.current && !isFocusScreenShare && isScreenShareEnabled && !toggling) {
            const stopShare = async () => {
                if (activeCropTrack) {
                    activeCropTrack.stop()
                    await room.localParticipant.unpublishTrack(activeCropTrack)
                    activeCropTrack = null
                } else {
                    await room.localParticipant.setScreenShareEnabled(false)
                }
            }
            stopShare()
        }
        prevFocusRef.current = isFocusScreenShare
    }, [isFocusScreenShare])

    useImperativeHandle(ref, () => ({
        startScreenShare: () => {
            if (!isFocusScreenShare && !toggling) handleToggle()
        },
    }), [isFocusScreenShare, toggling])

    const handleToggle = async () => {
        if (toggling) return
        setToggling(true)
        try {
            if (!isFocusScreenShare) {
                const useCrop = isCropSupported && cropTargetRef?.current

                if (useCrop) {
                    // Region Capture: crop screen share to target element only
                    const cropTarget = await CropTarget.fromElement(
                        cropTargetRef.current
                    )
                    const stream =
                        await navigator.mediaDevices.getDisplayMedia({
                            video: { displaySurface: "browser" },
                            preferCurrentTab: true,
                        })
                    const [videoTrack] = stream.getVideoTracks()
                    await videoTrack.cropTo(cropTarget)

                    const lvTrack = new LocalVideoTrack(videoTrack)
                    await room.localParticipant.publishTrack(lvTrack, {
                        source: Track.Source.ScreenShare,
                        name: "focus-screen-share",
                    })
                    activeCropTrack = lvTrack

                    // Handle browser "Stop sharing" for cropped track
                    videoTrack.onended = async () => {
                        try {
                            await room.localParticipant.unpublishTrack(lvTrack)
                        } catch (e) {
                            console.warn("Failed to unpublish crop track:", e)
                        }
                        activeCropTrack = null
                        dispatch(setFocusScreenShare(false))
                    }
                } else {
                    // Fallback: standard full-tab screen share
                    await room.localParticipant.setScreenShareEnabled(true, {
                        audio: true,
                        preferCurrentTab: true,
                        selfBrowserSurface: "include",
                    })
                }
                dispatch(setFocusScreenShare(true))
            } else {
                // Stop screen share
                if (activeCropTrack) {
                    activeCropTrack.stop()
                    await room.localParticipant.unpublishTrack(activeCropTrack)
                    activeCropTrack = null
                } else {
                    await room.localParticipant.setScreenShareEnabled(false)
                }
                dispatch(setFocusScreenShare(false))
            }
        } catch (e) {
            // User cancelled the browser screen share dialog or error occurred
            console.warn("Screen share toggle failed:", e)
        } finally {
            setToggling(false)
        }
    }

    const defaultColor = isFocusScreenShare ? theme.colors.error : theme.colors.greyAlt
    const color = overrideColor || defaultColor

    return (
        <Pressable
            onPress={handleToggle}
            disabled={toggling}
            style={({ pressed }) => [
                toolbarStyle,
                { opacity: pressed || toggling ? 0.5 : 1 },
                style,
            ]}
        >
            {!hideIcon && (
                <FontAwesome5
                    name={"chalkboard-teacher"}
                    size={14}
                    color={color}
                    solid
                />
            )}
            <TextM
                fontSize={fontSizeProp || 12}
                fontWeight={fontWeightProp || "Regular"}
                color={color}
                style={{ marginTop: label ? 0 : 2 }}
                wrap
            >
                {label || (isFocusScreenShare ? "Arrêter" : "Présenter")}
            </TextM>
        </Pressable>
    )
})

export default ScreenShareToggleButton
